import type {
	Cluster,
	ClustersRequestFilters
} from '~/core/entities/api/ApiClusters';

import { useEventBus } from '@vueuse/core';
import {
	usePropertySearchEventKey,
	usePropertySearchScope
} from '~/composables/propertySearch/context';
import { filterRequestKey } from '~/composables/propertySearch/store';
import { extractErrorLogContext, useLogger } from '~/composables/useLogger';
import { preparePropertySearchFilters } from '~/core/entities/property/filterPolicy';
import { EventBusKeys } from '~/core/helpers/symbols';
import { createSafeDebouncedFunction } from '~/utils';
import { nuxtClustersRepository } from '~~/adapters/repositories/nuxt/nuxtClustersRepository';
import { sanitizeFilterQuery } from '~~/utils/sanitizeFilterQuery';

interface UseMapClustersOptions {
	gmapRef: Ref<{ mapInstance?: google.maps.Map } | null>;
	clustersFilters?: {
		[key: string]: string | boolean | number;
	} & Partial<ClustersRequestFilters>;
}

const MIN_CLUSTER_ZOOM = 7;

type ClustersOptions = UseMapClustersOptions['clustersFilters'];
type ClustersRequest = Parameters<typeof nuxtClustersRepository.list>[0];
interface MapDimensions {
	width?: number;
	height?: number;
}

function clampZoom(zoom?: number) {
	return zoom && zoom < MIN_CLUSTER_ZOOM ? MIN_CLUSTER_ZOOM : zoom;
}

// Production request shape, kept byte-for-byte.
function legacyClusterRequest(
	filters: ClustersRequestFilters,
	mapDimensions: MapDimensions,
	options: ClustersOptions
): ClustersRequest {
	// Built apart so the extra API-only keys pass without an excess-property error.
	const request = {
		...filters,
		bounds: filters.bounds || undefined,
		zoom: clampZoom(filters.zoom) || undefined,
		mapWidth: mapDimensions.width,
		mapHeight: mapDimensions.height,
		transactionType: filters.transactionType,
		includePropertyIds: options?.includePropertyIds,
		includeH3Clusters: true,
		showHideProperties: options?.showHideProperties,
		showUnpublishedProperties: options?.showUnpublishedProperties,
		wishlistId: options?.wishlistId,
		h3Ids: [],
		commercialIds: []
	};
	return request;
}

function scopedClusterRequest(
	filters: ClustersRequestFilters,
	mapDimensions: MapDimensions,
	options: ClustersOptions
): ClustersRequest {
	const zoom = clampZoom(filters.zoom);
	return {
		...preparePropertySearchFilters({
			filters,
			target: 'clusters',
			includeH3Clusters: true
		}),
		...(Number.isFinite(zoom) ? { zoom } : {}),
		...(mapDimensions.width ? { mapWidth: mapDimensions.width } : {}),
		...(mapDimensions.height ? { mapHeight: mapDimensions.height } : {}),
		...(options?.includePropertyIds !== undefined
			? { includePropertyIds: options.includePropertyIds }
			: {}),
		...(options?.showHideProperties !== undefined
			? { showHideProperties: options.showHideProperties }
			: {})
	};
}

export function useMapClusters({
	gmapRef,
	clustersFilters
}: UseMapClustersOptions) {
	const logger = useLogger('MapClusters');
	const scope = usePropertySearchScope();
	let disposed = false;
	let pendingKey: string | undefined;
	let completed: { key: string; clusters: Cluster[] } | undefined;
	let pendingCompletion: Promise<void> | undefined;
	const clusters = ref<Cluster[]>([]);
	const isUpdatingClusters = ref(false);
	const selectedClustersData = ref<Set<string>>(new Set());
	const selectedClustersCache = ref<Map<string, Cluster>>(new Map());
	const markersKey = ref(0);

	let abortController: AbortController | null = null;

	const autoZoomBus = useEventBus(
		usePropertySearchEventKey(EventBusKeys.autoZoom)
	);

	function normalizeClusterCoordinates(coordinates: [number, number]): {
		lat: number;
		lng: number;
	} {
		const [first, second] = coordinates;
		return { lat: first, lng: second };
	}

	function handleClusterSelectionChanged(data: {
		clusterId: string;
		isSelected: boolean;
	}) {
		if (data.isSelected) {
			selectedClustersData.value.add(data.clusterId);
			const cluster = clusters.value.find(
				c => c.geometry.id === data.clusterId
			);
			if (cluster) {
				selectedClustersCache.value.set(data.clusterId, cluster);
			}
		} else {
			selectedClustersData.value.delete(data.clusterId);
			selectedClustersCache.value.delete(data.clusterId);
		}
	}

	function handleClusterDeselectedByResolution(data: { clusterId: string }) {
		selectedClustersData.value.delete(data.clusterId);
		selectedClustersCache.value.delete(data.clusterId);
	}

	function clearSelectedClusters() {
		selectedClustersData.value.clear();
		selectedClustersCache.value.clear();
	}

	const selectedH3Ids = computed(() => {
		return Array.from(selectedClustersData.value);
	});

	function incrementMarkersKey() {
		markersKey.value += 1;
	}

	function getMapDimensions(): { width?: number; height?: number } {
		if (!gmapRef.value?.mapInstance) {
			return {};
		}

		try {
			const mapDiv = gmapRef.value.mapInstance.getDiv();
			if (mapDiv) {
				return {
					width: mapDiv.offsetWidth || undefined,
					height: mapDiv.offsetHeight || undefined
				};
			}
		} catch (error) {
			logger.warn(
				'Error getting map dimensions',
				extractErrorLogContext(error)
			);
		}

		return {};
	}

	async function fetchClusters(
		buildRequest: () => ClustersRequest,
		key?: string
	) {
		if (abortController)
			abortController.abort('Request superseded by newer request');
		abortController = new AbortController();
		const currentController = abortController;
		let complete: () => void = () => {};
		if (key) {
			pendingKey = key;
			pendingCompletion = new Promise<void>(resolve => {
				complete = resolve;
			});
		}
		try {
			isUpdatingClusters.value = true;

			const [result, error] = await nuxtClustersRepository.list(
				buildRequest(),
				currentController.signal
			);

			if (currentController.signal.aborted) {
				return;
			}

			if (error) {
				logger.error('Error while fetching clusters from repository', {
					source: 'useMapClusters',
					...extractErrorLogContext(error)
				});
				return;
			}

			if (currentController === abortController) {
				applyClusters(result.data);
				if (key) completed = { key, clusters: result.data };

				if (result.isAutoZoom && result.calculatedZoom) {
					autoZoomBus.emit(result.calculatedZoom);
				}
			}
		} catch (err) {
			if (!currentController.signal.aborted) {
				logger.error('Unexpected error in updateClusters', {
					source: 'useMapClusters',
					...extractErrorLogContext(err)
				});
			}
		} finally {
			complete();
			if (
				currentController === abortController &&
				!currentController.signal.aborted
			) {
				isUpdatingClusters.value = false;
			}

			// Clean up the abort controller if this was the current one
			if (currentController === abortController) {
				abortController = null;
				pendingKey = undefined;
				pendingCompletion = undefined;
			}
		}
	}

	function applyClusters(data: Cluster[]) {
		const newClustersMap = new Map<string, Cluster>();
		data.forEach(cluster => {
			newClustersMap.set(cluster.geometry.id, cluster);
		});

		const selectedClusterIds = Array.from(selectedClustersData.value);
		selectedClusterIds.forEach(clusterId => {
			const updatedCluster = newClustersMap.get(clusterId);
			if (updatedCluster) {
				selectedClustersCache.value.set(clusterId, updatedCluster);
			}
		});

		clusters.value = data;
		incrementMarkersKey();
	}

	// The lab reuses an identical in-flight or last response instead of asking again.
	function updateScopedClusters(filters: ClustersRequestFilters) {
		if (disposed) return;
		const request = sanitizeFilterQuery(
			scopedClusterRequest(filters, getMapDimensions(), clustersFilters)
		);
		const key = filterRequestKey(request);
		if (key === pendingKey) return pendingCompletion;
		if (!pendingKey && key === completed?.key) {
			clusters.value = [...completed.clusters];
			return;
		}
		return fetchClusters(() => request, key);
	}

	const updateClusters = async (filters: ClustersRequestFilters) => {
		if (scope) return updateScopedClusters(filters);
		return fetchClusters(() =>
			legacyClusterRequest(filters, getMapDimensions(), clustersFilters)
		);
	};

	const debouncedUpdateClusters = createSafeDebouncedFunction(
		updateClusters,
		300,
		{
			maxWait: 1000
		}
	);

	onUnmounted(() => {
		disposed = true;
		if (abortController) {
			abortController.abort('Component unmounted');
		}
	});

	return {
		clusters,
		selectedClustersData: readonly(selectedClustersData),
		selectedH3Ids,
		markersKey,
		updateClusters,
		handleClusterSelectionChanged,
		handleClusterDeselectedByResolution,
		normalizeClusterCoordinates,
		clearSelectedClusters
	};
}
